import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { getNotifications, markNotificationAsRead } from "../utils/api-lists/notifications.api";
import { getPdfReportByPatient } from "../utils/api-lists/patient.api";
import { UserContext } from "../context/UserContext";

export const useNotifications = (limit = null) => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const prepareNotifications = async (rawNotifications) => {
    const prepared = await Promise.all(
      rawNotifications.map(async (n) => {
        if (n.reference_type === "pdf_upload") {
          const message = n.message;
          const patient_id = message.match(/patient_id=([^\s]+)/)?.[1];
          const path = message.match(/path=(.+)$/)?.[1];

          let pdfUrl = null;
          if (patient_id && path) {
            const patient_pdf = await getPdfReportByPatient(patient_id);
            const pdf = patient_pdf.pdf_reports.find((pdf) => pdf.path === path);
            pdfUrl = pdf?.url || null;
          }

          return {
            id: n.id,
            type: "pdf_upload",
            messageText: message.split("|")[0],
            patient_id,
            pdfUrl,
            is_read: n.is_read,
            title: "PDF Report Ready for your Review",
            created_at: n.created_at,
          };
        }

        if (n.reference_type === "report") {
          return {
            id: n.id,
            type: "report",
            messageText: n.message.split("|")[0],
            patient_id: n.patient_id,
            reference_id: n.reference_id,
            is_read: n.is_read,
            title: "Report Ready for your Review",
            created_at: n.created_at,
          };
        }

        return {
          id: n.id,
          type: n.reference_type,
          messageText: n.message,
          is_read: n.is_read,
          created_at: n.created_at,
        };
      })
    );

    return prepared;
  };

  const loadNotifications = async () => {
    if (!user?.id) return;

    setLoading(true);
    try {
      const response = await getNotifications(user.id);

      let sorted = response.notifications.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      if (limit) {
        sorted = sorted.slice(0, limit);
      }

      const prepared = await prepareNotifications(sorted);
      setNotifications(prepared);
    } catch (error) {
      console.error("Error loading notifications:", error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    if (notification.type === "report") {
      navigate(
        `/patient-cabinet/${notification.patient_id}/skin-report/${notification.reference_id}`
      );
    } else if (notification.type === "pdf_upload" && notification.pdfUrl) {
      window.open(notification.pdfUrl, "_blank");
    }

    await markNotificationAsRead(user.id, notification.id);

    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notification.id ? { ...n, is_read: true } : n
      )
    );
  };

  const handleMarkAsRead = async (notification) => {
    if (!notification.is_read) return;
    await markNotificationAsRead(user.id, notification.id);
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === notification.id ? { ...n, is_read: true } : n
      )
    );
  }

  useEffect(() => {
    loadNotifications();
  }, [user?.id]);

  return { notifications, loading, handleNotificationClick, handleMarkAsRead };
};